import React, { useState, useEffect } from 'react';
import { Search, MapPin, Star, DollarSign, Package, Filter, ChevronRight, Heart, Clock, Briefcase, Loader2, X, AlertCircle, ScrollText, Home, Car, Building2, HandHelping, Flame, ShieldAlert } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { jobsAPI } from '../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Polygon } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Delivery type icons and colors
const DELIVERY_TYPE_CONFIG = {
  houses: { icon: Home, color: 'bg-green-500', label: 'Houses' },
  apartments: { icon: Building2, color: 'bg-blue-500', label: 'Apartments' },
  cars: { icon: Car, color: 'bg-purple-500', label: 'Cars' },
  hands: { icon: HandHelping, color: 'bg-orange-500', label: 'Hands' },
};

const JobSearchPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [savedJobs, setSavedJobs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showContractPopup, setShowContractPopup] = useState(false);
  const [showSafetyPopup, setShowSafetyPopup] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationError, setLocationError] = useState(null);
  const [userStats, setUserStats] = useState({ 
    jobsCompleted: 0, 
    flyersDelivered: 0,
    isPro: false,
    canAcceptMoreJobs: true,
    activeJobsCount: 0,
    maxActiveJobs: 1
  });
  const { t } = useLanguage();
  const { isAuthenticated, user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  // Max distance for job visibility (in miles)
  const MAX_JOB_DISTANCE_MILES = 50;

  // Pro unlock requirements
  const PRO_JOBS_REQUIRED = 60;
  const PRO_FLYERS_REQUIRED = 30000;

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Get user's location on mount
  useEffect(() => {
    const getUserLocation = () => {
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by your browser');
        setLocationLoading(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          setLocationError('Unable to get your location. Showing all available jobs.');
          setLocationLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    };

    getUserLocation();
  }, []);

  const filters = [
    { id: 'all', labelKey: 'jobs_filter_all' },
    { id: 'nearby', labelKey: 'jobs_filter_nearby' },
    { id: 'highpay', labelKey: 'jobs_filter_highpay' },
    { id: 'easy', labelKey: 'jobs_filter_easy' },
    { id: 'urgent', labelKey: 'jobs_filter_urgent' },
  ];

  // Fetch user stats for Pro progress
  useEffect(() => {
    const fetchUserStats = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, getAuthHeader());
        const data = await response.json();
        setUserStats({
          jobsCompleted: data.jobs_completed || 0,
          flyersDelivered: data.total_deliveries || 0,
          isPro: data.is_pro || false,
          canAcceptMoreJobs: data.can_accept_more_jobs !== false,
          activeJobsCount: data.active_jobs_count || 0,
          maxActiveJobs: data.max_active_jobs || 1
        });
      } catch (error) {
        console.error('Error fetching user stats:', error);
      }
    };
    fetchUserStats();
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchJobs = async () => {
      // Wait for location to be determined before fetching jobs
      if (locationLoading) return;
      
      try {
        setLoading(true);
        const response = await jobsAPI.getAvailableJobs();
        // Map API response to expected format and calculate distances
        const mappedJobs = response.data.map(job => {
          // Calculate distance from user's location to job center
          let distanceMiles = null;
          if (userLocation && job.map_center && job.map_center.length === 2) {
            distanceMiles = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              job.map_center[0],
              job.map_center[1]
            );
          }
          
          return {
            id: job.id,
            title: job.title,
            client: job.client,
            location: job.location,
            deliveries: job.deliveries,
            payPerFlyer: job.pay_per_flyer,
            totalPay: job.total_pay,
            bonusPay: job.bonus_pay || 0,
            distanceMiles: distanceMiles,
            distance: distanceMiles !== null ? `${distanceMiles.toFixed(1)} miles` : 'Unknown',
            difficulty: job.difficulty || 'Easy',
            rating: 4.7,
            deadline: job.deadline,
            flyerType: job.flyer_type,
            deliveryType: job.delivery_type || 'houses',
            mapCenter: job.map_center,
            deliveryBoundary: job.delivery_boundary,
            deliveredHouseIds: job.delivered_house_ids || [],
            pickupLocation: job.pickup_location,
            pickupDate: job.pickup_date
          };
        });
        
        // Filter jobs within 50 miles (or show all if no location)
        const nearbyJobs = userLocation 
          ? mappedJobs.filter(job => 
              job.distanceMiles === null || job.distanceMiles <= MAX_JOB_DISTANCE_MILES
            )
          : mappedJobs;
        
        setJobs(nearbyJobs);
      } catch (err) {
        console.error('Error fetching jobs:', err);
        toast.error(t('error') + ': ' + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [t, userLocation, locationLoading]);

  const filteredJobs = jobs.filter(job => {
    if (searchQuery) {
      return job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
             job.client.toLowerCase().includes(searchQuery.toLowerCase()) ||
             job.location.toLowerCase().includes(searchQuery.toLowerCase());
    }
    if (activeFilter === 'all') return true;
    if (activeFilter === 'highpay') return job.totalPay > 80;
    if (activeFilter === 'easy') return job.difficulty === 'Easy';
    if (activeFilter === 'nearby') return job.distanceMiles !== null && job.distanceMiles < 5;
    return true;
  });

  const toggleSaveJob = (jobId) => {
    setSavedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const handleRequestJob = async (jobId) => {
    if (!isAuthenticated) {
      toast.error(t('auth_sign_in') + ' to request jobs');
      navigate('/login');
      return;
    }

    // Check if contract is signed
    if (!user?.contract_signed) {
      setShowContractPopup(true);
      return;
    }

    // Check if safety rules are agreed
    if (!user?.safety_agreed) {
      setShowSafetyPopup(true);
      return;
    }

    try {
      setAccepting(jobId);
      await jobsAPI.requestJob(jobId);
      toast.success('Job request submitted! An admin will review your request.');
      // Remove requested job from list (it's no longer available for others)
      setJobs(prev => prev.filter(j => j.id !== jobId));
      // Close the job detail modal if open
      setSelectedJob(null);
    } catch (err) {
      console.error('Error requesting job:', err);
      toast.error(err.response?.data?.detail || 'Failed to request job');
    } finally {
      setAccepting(null);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-700';
      case 'Moderate': return 'bg-yellow-100 text-yellow-700';
      case 'Hard': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-2" />
          <p className="text-gray-500">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="job-search-page">
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-4 border-b border-gray-100">
        <h1 className="text-2xl font-bold text-gray-900 mb-4" data-testid="jobs-title">{t('jobs_find')}</h1>
        
        {/* Search Bar */}
        <div className="relative">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder={t('jobs_search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-12 py-3 bg-gray-100 border-0 rounded-xl text-sm"
            data-testid="jobs-search-input"
          />
          <button className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center" data-testid="jobs-filter-btn">
            <Filter size={16} className="text-white" />
          </button>
        </div>

        {/* Filter Pills */}
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2 -mx-5 px-5 scrollbar-hide">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              data-testid={`filter-${filter.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeFilter === filter.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t(filter.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* No Jobs State */}
      {filteredJobs.length === 0 && (
        <div className="px-5 mt-12 text-center">
          <Briefcase size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('jobs_no_jobs')}</h3>
          <p className="text-gray-500 text-sm">{t('jobs_check_back')}</p>
        </div>
      )}

      {/* Featured Job (first job if available) */}
      {filteredJobs.length > 0 && (() => {
        const featuredJob = filteredJobs[0];
        const featuredTypeConfig = DELIVERY_TYPE_CONFIG[featuredJob.deliveryType] || DELIVERY_TYPE_CONFIG.houses;
        const FeaturedIcon = featuredTypeConfig.icon;
        
        return (
        <div className="px-5 mt-5">
          <div className="relative rounded-2xl overflow-hidden h-48" data-testid="featured-job">
            {/* Icon Background */}
            <div className={`w-full h-full ${featuredTypeConfig.color} flex items-center justify-center`}>
              <FeaturedIcon size={80} className="text-white/30" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-orange-500 text-white">{t('jobs_featured')}</Badge>
                <Badge className="bg-white/20 text-white">{featuredTypeConfig.label}</Badge>
              </div>
              <h3 className="text-xl font-bold mb-1">{featuredJob.title}</h3>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <Package size={14} /> {featuredJob.deliveries} {featuredJob.deliveryType === 'apartments' ? 'doors' : t('jobs_flyers')}
                </span>
                <span className="flex items-center gap-1 text-green-400">
                  <DollarSign size={14} /> ${featuredJob.totalPay.toFixed(2)}
                </span>
                <span className="flex items-center gap-1">
                  <Star size={14} className="text-yellow-400" fill="#facc15" /> {featuredJob.rating}
                </span>
              </div>
            </div>
            <button 
              onClick={() => toggleSaveJob(featuredJob.id)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center" 
              data-testid="save-featured-job-btn"
            >
              <Heart 
                size={20} 
                className={savedJobs.includes(featuredJob.id) ? 'text-orange-500 fill-orange-500' : 'text-white'} 
              />
            </button>
          </div>
        </div>
        );
      })()}

      {/* Available Jobs Section */}
      <div className="px-5 mt-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold text-gray-900" data-testid="available-jobs-title">{t('jobs_title')}</h2>
          <span className="text-sm text-gray-500">{filteredJobs.length} {t('nav_jobs').toLowerCase()}</span>
        </div>
        
        {/* Location Status Banner */}
        <div className="mb-4">
          {locationLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
              <Loader2 size={14} className="animate-spin" />
              <span>Getting your location...</span>
            </div>
          ) : locationError ? (
            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
              <AlertCircle size={14} />
              <span>{locationError}</span>
            </div>
          ) : userLocation ? (
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg" data-testid="location-status">
              <MapPin size={14} />
              <span>Showing jobs within {MAX_JOB_DISTANCE_MILES} miles of your location</span>
            </div>
          ) : null}
        </div>

        {/* Jobs List */}
        <div className="space-y-4" data-testid="jobs-list">
          {filteredJobs.map((job, index) => {
            const typeConfig = DELIVERY_TYPE_CONFIG[job.deliveryType] || DELIVERY_TYPE_CONFIG.houses;
            const TypeIcon = typeConfig.icon;
            
            return (
            <Card
              key={job.id}
              onClick={() => setSelectedJob(job)}
              className="bg-white border-0 shadow-sm overflow-hidden animate-slide-up cursor-pointer hover:shadow-md transition-all"
              style={{ animationDelay: `${index * 0.05}s` }}
              data-testid={`job-card-${job.id}`}
            >
              <div className="flex">
                {/* Delivery Type Icon */}
                <div className={`w-28 h-28 ${typeConfig.color} flex-shrink-0 flex flex-col items-center justify-center relative`}>
                  <TypeIcon size={40} className="text-white" />
                  <span className="text-white text-xs font-medium mt-2">{typeConfig.label}</span>
                  {/* Bonus Pay Badge */}
                  {job.bonusPay > 0 && (
                    <div className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-0.5 shadow-lg animate-pulse" data-testid={`bonus-badge-${job.id}`}>
                      <Flame size={10} className="text-white" />
                      +${job.bonusPay.toFixed(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1 p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{job.title}</h3>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Briefcase size={12} /> {job.client}
                      </p>
                      {/* Pickup Materials Required Badge */}
                      {(job.pickupLocation || job.pickupDate) && (
                        <div className="flex items-center gap-1 mt-1">
                          <Badge className="bg-orange-100 text-orange-700 text-[10px] font-medium border border-orange-200 px-1.5 py-0.5">
                            <Package size={10} className="mr-0.5" />
                            Pickup Required
                          </Badge>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSaveJob(job.id);
                      }}
                      className="ml-2 flex-shrink-0"
                      data-testid={`save-job-${job.id}`}
                    >
                      <Heart
                        size={18}
                        className={savedJobs.includes(job.id) ? 'text-orange-500 fill-orange-500' : 'text-gray-300'}
                      />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-gray-600 flex items-center gap-1">
                      <Package size={12} className="text-orange-500" /> {job.deliveries} {job.deliveryType === 'apartments' ? 'doors' : t('jobs_flyers')}
                    </span>
                    <span className="text-xs text-gray-600 flex items-center gap-1">
                      <MapPin size={12} className="text-blue-500" /> {job.distance}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <Badge className={`${getDifficultyColor(job.difficulty)} text-xs font-medium`}>
                        {job.difficulty}
                      </Badge>
                      {job.bonusPay > 0 && (
                        <Badge className="bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-700 text-xs font-bold border border-orange-300">
                          <Flame size={10} className="mr-0.5" /> BONUS
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <span className="text-sm font-bold text-green-600">${(job.totalPay + job.bonusPay).toFixed(2)}</span>
                        {job.bonusPay > 0 && (
                          <p className="text-[10px] text-orange-600">incl. ${job.bonusPay.toFixed(0)} bonus</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestJob(job.id);
                        }}
                        disabled={accepting === job.id}
                        className="px-3 py-1 bg-orange-500 text-white text-xs rounded-full font-medium hover:bg-orange-600 disabled:opacity-50"
                        data-testid={`request-job-${job.id}`}
                      >
                        {accepting === job.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'Request'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          );
          })}
        </div>
      </div>

      {/* Unlock Pro Box */}
      <div className="px-5 mt-6 mb-4">
        {userStats.isPro ? (
          /* Pro Status Achieved */
          <div className="bg-gradient-to-br from-yellow-500 via-amber-500 to-orange-500 rounded-2xl p-5 text-white relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            {/* Content */}
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Star size={22} className="text-yellow-500" fill="#eab308" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">PRO STATUS</h3>
                  <p className="text-xs text-white/80">Unlocked!</p>
                </div>
              </div>
              
              <p className="text-lg font-semibold mb-3">
                You can accept up to {userStats.maxActiveJobs} jobs at once!
              </p>

              {/* Active Jobs Status */}
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Active Jobs</span>
                  <span className="text-lg font-bold">{userStats.activeJobsCount} / {userStats.maxActiveJobs}</span>
                </div>
                <div className="h-2 bg-white/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${(userStats.activeJobsCount / userStats.maxActiveJobs) * 100}%` }}
                  ></div>
                </div>
                <p className="text-xs text-white/80 mt-2">
                  {userStats.canAcceptMoreJobs 
                    ? `You can accept ${userStats.maxActiveJobs - userStats.activeJobsCount} more job${userStats.maxActiveJobs - userStats.activeJobsCount !== 1 ? 's' : ''}`
                    : 'Complete a job to accept more'}
                </p>
              </div>

              {/* Stats */}
              <div className="flex gap-4 mt-4 text-sm">
                <div>
                  <p className="font-bold text-lg">{userStats.jobsCompleted}</p>
                  <p className="text-white/70">Jobs Done</p>
                </div>
                <div>
                  <p className="font-bold text-lg">{userStats.flyersDelivered.toLocaleString()}</p>
                  <p className="text-white/70">Deliveries</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Progress to Pro */
          <div className="bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 rounded-2xl p-5 text-white relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            {/* Content */}
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-yellow-400 rounded-lg flex items-center justify-center">
                  <Star size={18} className="text-yellow-800" fill="#854d0e" />
                </div>
                <h3 className="text-xl font-bold">Unlock Pro</h3>
              </div>
              
              <p className="text-lg font-semibold text-yellow-300 mb-1">
                ACCEPT MULTIPLE JOBS AT ONE TIME!
              </p>
              <p className="text-sm text-white/80 mb-4">
                Complete 60 jobs or 30,000 deliveries to unlock
              </p>

              {/* Progress Section */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-sm font-medium mb-3">Progress</p>
                
                {/* Jobs Progress Bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span>{userStats.jobsCompleted}/{PRO_JOBS_REQUIRED} jobs</span>
                    <span>{Math.round((userStats.jobsCompleted / PRO_JOBS_REQUIRED) * 100)}%</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min((userStats.jobsCompleted / PRO_JOBS_REQUIRED) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Flyers Progress Bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span>{userStats.flyersDelivered.toLocaleString()}/{PRO_FLYERS_REQUIRED.toLocaleString()} deliveries</span>
                    <span>{Math.round((userStats.flyersDelivered / PRO_FLYERS_REQUIRED) * 100)}%</span>
                  </div>
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min((userStats.flyersDelivered / PRO_FLYERS_REQUIRED) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Remaining */}
                <p className="text-xs text-white/70">
                  {Math.max(PRO_JOBS_REQUIRED - userStats.jobsCompleted, 0)} jobs remaining • {Math.max(PRO_FLYERS_REQUIRED - userStats.flyersDelivered, 0).toLocaleString()} deliveries remaining
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom padding for nav */}
      <div className="pb-24" />

      {/* Job Detail Modal with Map */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
          <div className="bg-white w-full max-w-[430px] rounded-t-3xl overflow-hidden animate-slide-up" data-testid="job-detail-modal">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b">
              <h3 className="text-lg font-bold text-gray-900">{selectedJob.title}</h3>
              <button
                onClick={() => setSelectedJob(null)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                data-testid="close-job-modal-btn"
              >
                <X size={18} className="text-gray-600" />
              </button>
            </div>

            {/* Map - Use key to force remount and prevent stale Leaflet state */}
            <div className="h-64 relative">
              {selectedJob.mapCenter && selectedJob.deliveryBoundary ? (
                <MapContainer
                  key={`job-map-${selectedJob.id}`}
                  center={selectedJob.mapCenter}
                  zoom={14}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={false}
                  attributionControl={false}
                  whenReady={() => {}}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Polygon
                    positions={selectedJob.deliveryBoundary}
                    pathOptions={{
                      color: '#f97316',
                      weight: 3,
                      fillColor: '#f97316',
                      fillOpacity: 0.2,
                    }}
                  />
                </MapContainer>
              ) : (
                <div className="h-full bg-gray-100 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <MapPin size={32} className="mx-auto mb-2" />
                    <p className="text-sm">Map not available</p>
                  </div>
                </div>
              )}
              <div className="absolute top-3 left-3 bg-orange-500 text-white px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1">
                <MapPin size={12} />
                Delivery Zone
              </div>
            </div>

            {/* Job Details */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-500">{selectedJob.client}</p>
                  <p className="text-xs text-gray-400 mt-1">{selectedJob.location}</p>
                </div>
                <Badge className={`${getDifficultyColor(selectedJob.difficulty)} text-xs font-medium`}>
                  {selectedJob.difficulty}
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <Package size={20} className="mx-auto text-orange-500 mb-1" />
                  <p className="text-lg font-bold text-gray-900">{selectedJob.deliveries}</p>
                  <p className="text-xs text-gray-500">{t('jobs_flyers')}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <DollarSign size={20} className="mx-auto text-green-500 mb-1" />
                  <p className="text-lg font-bold text-gray-900">${selectedJob.totalPay.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">{t('jobs_total_pay')}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <MapPin size={20} className="mx-auto text-blue-500 mb-1" />
                  <p className="text-lg font-bold text-gray-900">{selectedJob.distance}</p>
                  <p className="text-xs text-gray-500">Away</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setSelectedJob(null)}
                  className="flex-1"
                  data-testid="close-detail-btn"
                >
                  {t('jobs_close')}
                </Button>
                <Button
                  onClick={() => {
                    handleRequestJob(selectedJob.id);
                  }}
                  disabled={accepting === selectedJob.id}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="request-job-modal-btn"
                >
                  {accepting === selectedJob.id ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Request Job
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contract Required Popup */}
      {showContractPopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
          <Card className="bg-white p-6 max-w-sm w-full rounded-2xl" data-testid="contract-required-popup">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <ScrollText size={24} className="text-orange-600" />
              </div>
              <button
                onClick={() => setShowContractPopup(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-contract-popup-btn"
              >
                <X size={20} />
              </button>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('contract_required')}</h3>
            <p className="text-gray-600 text-sm mb-6">{t('contract_required_message')}</p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowContractPopup(false)}
                className="flex-1"
                data-testid="cancel-contract-popup-btn"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={() => {
                  setShowContractPopup(false);
                  navigate('/profile');
                }}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="go-to-settings-contract-btn"
              >
                {t('contract_go_to_settings')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Safety Rules Required Popup */}
      {showSafetyPopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
          <Card className="bg-white p-6 max-w-sm w-full rounded-2xl" data-testid="safety-required-popup">
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <ShieldAlert size={24} className="text-red-600" />
              </div>
              <button
                onClick={() => setShowSafetyPopup(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-safety-popup-btn"
              >
                <X size={20} />
              </button>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Safety Rules Required</h3>
            <p className="text-gray-600 text-sm mb-6">
              You must read and agree to all Safety & Delivery Rules before accepting jobs. 
              This ensures you understand how to deliver safely and professionally.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSafetyPopup(false)}
                className="flex-1"
                data-testid="cancel-safety-popup-btn"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={() => {
                  setShowSafetyPopup(false);
                  navigate('/profile');
                }}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                data-testid="go-to-settings-safety-btn"
              >
                Go to Safety Rules
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default JobSearchPage;
